import { createEntityAdapter, createSelector, createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { uploadPrivateAsset } from '@ff-os/pdp-shared-redux/apis/private-assets/uploadPrivateAsset';
import { Area } from '@ff-os/shared-utils/types/Area';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import cuid from 'cuid';
import { PhotoPrintsPdpState } from '../store';
import { handleFulfilled, handlePending, handleRejected } from './apis/privateAssetHandling';

const adapter = createEntityAdapter<PhotoPrintsJson.Photo>();
const adapterSelectors = adapter.getSelectors<PhotoPrintsPdpState>(s => s.project.photos);

export const { actions, reducer } = createSlice({
    name: 'photos',
    initialState: adapter.getInitialState(),
    reducers: {
        setAll: adapter.setAll,
        add: adapter.addOne,
        addMany: adapter.addMany,
        updateOne: adapter.updateOne,
        remove: adapter.removeOne,
        removeMany: adapter.removeMany,

        setQuantity: (state, action: PayloadAction<{ photoId: string; quantity: number }>) => {
            const { photoId, quantity } = action.payload;
            state.entities[photoId]!.quantity = quantity;
        },

        move: (state, action: PayloadAction<{ photoId: string; groupId: string }>) => {
            const { photoId, groupId } = action.payload;
            state.entities[photoId]!.groupId = groupId;
        },

        copy: (state, action: PayloadAction<{ photoId: string; groupId: string }>) => {

            const { photoId, groupId } = action.payload;

            adapter.addOne(state, {
                ...state.entities[photoId]!,
                groupId: groupId,
                id: cuid()
            });
        },

        updateCrop: (state, action: PayloadAction<{ photoId: string; crop: Area }>) => {
            const { photoId, crop } = action.payload;
            const photo = state.entities[photoId]! as Draft<PhotoPrintsJson.ProcessedPhoto>;
            photo.crop = crop;
        },

        updateFilter: (state, action: PayloadAction<{ photoId: string; filter: string | null }>) => {
            const { photoId, filter } = action.payload;
            const photo = state.entities[photoId]! as Draft<PhotoPrintsJson.ProcessedPhoto>;
            photo.filter = filter;
        }
    },

    extraReducers: builder => {
        builder.addCase(uploadPrivateAsset.pending, handlePending);
        builder.addCase(uploadPrivateAsset.rejected, handleRejected);
        builder.addCase(uploadPrivateAsset.fulfilled, handleFulfilled);
    }
});

export const addOneReducer = adapter.addOne;

export const selectHasErrorByGroupId = createSelector([
    (state: PhotoPrintsPdpState) => selectAllPhotos(state),
    (state: PhotoPrintsPdpState, groupId: string) => groupId
], (photos, groupId) => {
    return photos
        .filter(p => p.groupId === groupId)
        .some(p => p.status === 'error');
});

export const selectPhotoIdsByGroupId = createSelector([
    (state: PhotoPrintsPdpState) => selectAllPhotos(state),
    (state: PhotoPrintsPdpState, groupId: string) => groupId
], (photos, groupId) => {
    return photos
        .filter(p => p.groupId === groupId)
        .map(p => p.id);
});

export const selectPhotosByGroupId = createSelector([
    (state: PhotoPrintsPdpState) => adapterSelectors.selectAll(state),
    (state: PhotoPrintsPdpState, groupId: string) => groupId
], (photos, groupId) => {
    return Object.values(photos).filter(p => p!.groupId === groupId);
});

export const selectPhotoById = adapterSelectors.selectById;
export const selectAllPhotos = adapterSelectors.selectAll;

export const selectAllProcessedPhotos = createSelector([
    selectAllPhotos
], (photos) => photos.filter(p => p.processed) as PhotoPrintsJson.ProcessedPhoto[]);